import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api/client'
import type { ForgeModule } from '@/components/layout/AppShell'

const MODULE_META: Record<string, { label: string; description: string }> = {
  monitoring:  { label: 'Monitoring',       description: 'Pipeline run history, health checks and alert rules' },
  contracts:   { label: 'Contracts',        description: 'Customer agreements and contract lifecycle' },
  process:     { label: 'Process',          description: 'Internal process documentation and workflows' },
  planning:    { label: 'Project Planning', description: 'Project phases, milestones and planning boards' },
  time:        { label: 'Time Reporting',   description: 'Staff timesheets, approvals and payroll sync' },
  invoicing:   { label: 'Invoicing',        description: 'Invoice generation and billing management' },
  sales:       { label: 'Sales',            description: 'Pipeline, opportunities and deal tracking' },
  clementine:  { label: 'Clementine Host',  description: 'Clementine subscription and user management' },
}

export default function ModulesPage() {
  const queryClient = useQueryClient()

  const { data: modules = [], isLoading } = useQuery({
    queryKey: ['forge-modules'],
    queryFn: () => api.get<ForgeModule[]>('/modules'),
    staleTime: 10 * 60 * 1000,
  })

  const toggle = useMutation({
    mutationFn: ({ key, isProd }: { key: string; isProd: boolean }) =>
      api.patch(`/modules/${key}`, { isProd }),
    onSuccess: (_data, { key, isProd }) => {
      queryClient.invalidateQueries({ queryKey: ['forge-modules'] })
      toast.success(`${MODULE_META[key]?.label ?? key} marked as ${isProd ? 'production' : 'development'}`)
    },
    onError: () => toast.error('Failed to update module status'),
  })

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Settings</div>
          <h1 className="page-title">Modules</h1>
          <div className="page-subtitle">
            Control which modules are visible to all users. Dev modules are only visible to admins.
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="muted" style={{ padding: '32px 0' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {modules.map(m => {
            const meta = MODULE_META[m.key] ?? { label: m.displayName, description: '' }
            const isToggling = toggle.isPending && (toggle.variables as { key: string })?.key === m.key
            return (
              <div
                key={m.key}
                className="card"
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '14px 18px', borderRadius: 8,
                  opacity: isToggling ? 0.6 : 1,
                  transition: 'opacity 150ms',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: 'var(--ns-surface-2)',
                  border: '1px solid var(--ns-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Layers size={16} style={{ color: 'var(--ns-text-muted)' }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{meta.label}</span>
                    {!m.isProd && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                        color: 'var(--ns-brand)', opacity: 0.85,
                        background: 'color-mix(in oklch, var(--ns-brand) 12%, transparent)',
                        borderRadius: 3, padding: '1px 5px',
                      }}>DEV</span>
                    )}
                  </div>
                  {meta.description && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{meta.description}</div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: m.isProd ? 'var(--ns-success)' : 'var(--ns-text-muted)' }}>
                    {m.isProd ? 'Production' : 'Dev only'}
                  </span>
                  <button
                    role="switch"
                    aria-checked={m.isProd}
                    disabled={isToggling}
                    onClick={() => toggle.mutate({ key: m.key, isProd: !m.isProd })}
                    style={{
                      width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                      background: m.isProd
                        ? 'var(--ns-brand)'
                        : 'var(--ns-surface-2)',
                      outline: '1px solid var(--ns-border)',
                      position: 'relative', transition: 'background 200ms',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3,
                      left: m.isProd ? 21 : 3,
                      width: 16, height: 16, borderRadius: '50%',
                      background: m.isProd ? '#fff' : 'var(--ns-text-muted)',
                      transition: 'left 200ms',
                    }} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
